import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { customerAPI } from '../../services/api';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import TableTopControls from "../../components/TableTopControls";
import TableBottomControls from "../../components/TableBottomControls";
import ConfirmModal from "../../components/ConfirmModal";
import { Plus, Edit, Trash2 } from 'lucide-react';

export default function CustomersAdmin() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', address: '' });
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endItem = Math.min(currentPage * rowsPerPage, totalItems);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const resetAndReload = () => { setSearch(''); setCurrentPage(1); };

  const fetchCustomers = useCallback(async (page, size, searchValue) => {
    try {
      const { data } = await customerAPI.get(page, size, searchValue);

      setCustomers(data.data);
      setTotalItems(data.total);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, []);

  // pagination effect
  useEffect(() => {
    fetchCustomers(currentPage, rowsPerPage, search);
  }, [currentPage, rowsPerPage, search, fetchCustomers]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        addresses: formData.address
          ? [{ street: formData.address, city: 'City', area: 'Area', is_primary: true }]
          : []
      };

      if (editingCustomer) {
        await customerAPI.update(editingCustomer.id, payload);
        toast.success('Customer updated successfully');
      } else {
        await customerAPI.create(payload);
        toast.success('Customer created successfully');
      }

      setShowForm(false);
      setEditingCustomer(null);
      setFormData({ name: '', phone: '', email: '', address: '' });

      fetchCustomers(currentPage, rowsPerPage, search);

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);

    setFormData({
      name: customer.name,
      phone: customer.phone,
      email: customer.email || '',
      address: customer.addresses?.[0]?.street || ''
    });

    setShowForm(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await customerAPI.delete(deleteId);
      toast.success("Customer deleted successfully");
      resetAndReload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  return (
    <AdminLayout activeItem="Customers">
      <header className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-medium text-brand-primary font-outfit">Customers</h1>
          {!isSuperAdmin && (
            <button onClick={() => {
                setShowForm(true)
                setEditingCustomer(null);
                setFormData({ name: '', phone: '', email: '', address: '' });
              }}
              className="flex items-center bg-brand-accent text-white hover:bg-brand-accent-hover px-6 py-2 shadow-sm transition-all duration-200"
              data-testid="add-customer-button">
              <Plus className="h-5 w-5 mr-2" /> Add Customer
            </button>
          )}
        </div>
      </header>

      <main className="p-8" data-testid="customers-admin-page">
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="customer-form-modal"
            onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}
            onKeyDown={(e) => { if (e.key === 'Escape') setShowForm(false); }}>
            <div className="bg-white p-8 max-w-md w-full mx-4 border border-slate-200">
              <h2 className="text-xl font-medium text-brand-primary mb-6">
                {editingCustomer ? "Edit Customer" : "Add Customer"}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                  <input type="text" required value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent"
                    data-testid="customer-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input type="tel" required value={formData.phone} minLength={11} maxLength={11}
                    onChange={(e) => { 
                      // allow digits only, max 11
                      const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                      setFormData({ ...formData, phone: value });
                    }}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent"
                    data-testid="customer-phone-input" inputMode="numeric" pattern="[0-9]{11}"/>
                    {formData.phone.length > 0 && formData.phone.length !== 11 && (
                      <p className="text-xs text-red-500 mt-1">
                        Phone number must be exactly 11 digits
                      </p>
                    )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email (Optional)</label>
                  <input type="email" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent"
                    data-testid="customer-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                  <textarea required maxLength={100} value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows={3} className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent"
                    data-testid="customer-address-input" />
                  <div className="text-xs text-slate-500 text-right mt-1">
                    {formData.address.length}/100
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 bg-brand-accent text-white hover:bg-brand-accent-hover px-6 py-2 shadow-sm transition-all duration-200" data-testid="customer-submit-button">
                    {editingCustomer ? "Update" : "Create"}
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); setEditingCustomer(null); }}
                    className="flex-1 border border-slate-300 text-slate-700 hover:bg-slate-50 px-6 py-2 transition-all duration-200">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200">
          <TableTopControls
            search={search}
            setSearch={(value) => {
              setSearch((prev) => {
                if (prev !== value) {
                  setCurrentPage(1);
                  return value;
                }
                return prev;
              });
            }}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(value) => {
              setRowsPerPage(value);
              setCurrentPage(1);
            }}
          />

          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
              <div className="animate-spin h-10 w-10 border-b-2 border-brand-accent"></div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="customers-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {isSuperAdmin && (
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">
                      Franchise
                    </th>
                  )}
                  <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Phone</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Address</th>
                  {!isSuperAdmin && (
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {customers.length === 0 ? (
                  <tr><td colSpan={5} className="px-6 py-4 text-center text-slate-500">No customers found.</td></tr>
                ) : (
                  customers.map((customer) => (
                    <tr key={customer.id} className="border-b border-slate-100 hover:bg-slate-50">
                      {isSuperAdmin && (
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {customer.franchise_name || '-'}
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm font-medium text-slate-800">{customer.name}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{customer.phone}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{customer.email || '-'}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{customer.addresses?.[0]?.street || '-'}</td>
                      {!isSuperAdmin && (
                        <td className="px-6 py-4 text-left text-sm text-slate-600">
                          <button onClick={() => handleEdit(customer)}
                            className="inline-flex items-center py-1 text-sm text-brand-accent hover:text-brand-accent-hover mr-2"
                            data-testid={`edit-customer-${customer.id}`}>
                            <Edit className="h-4 w-4 mr-1" /> Edit
                          </button>
                          <button onClick={() => handleDeleteClick(customer.id)}
                            className="inline-flex items-center pl-3 py-1 text-sm text-red-600 hover:text-red-700"
                            data-testid={`delete-customer-${customer.id}`}>
                            <Trash2 className="h-4 w-4 mr-1" /> Delete
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <TableBottomControls
            currentPage={currentPage}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
            startItem={startItem}
            endItem={endItem}
            totalItems={totalItems}
          />
        </div>

        <ConfirmModal
          open={confirmOpen}
          title="Delete Customer"
          message="Are you sure you want to delete this customer? This action cannot be undone."
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          confirmText="Delete"
        />

      </main>
    </AdminLayout>
    
  );
  
}
